import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Hotel } from 'app/shared/model/hotel';
import { UtilService } from 'app/shared/util.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from 'app/core-lib/dialogs/modal/modal.service';
import { CoreLibEmailRateModalComponent } from 'app/core-lib/dialogs/email-rate-modal/email-rate-modal.component';
import { FollowupPropertiesService } from 'app/inter-chain/followup/properties/followup-properties.service';
import { canLoadData, getRequestParams, getQualification } from '../followup-summary-widgets.utils';
import { Qualification } from '../followup-summary-widgets.utils';
import { getSimpleCell } from '../followup-summary-widgets-table.utils';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'mh-surveys-summary-answers-rate',
  templateUrl: './answers-rate-widget.component.html',
  standalone: false,
})
export class AnswersRateWidgetComponent implements OnChanges {
  @Input() chainFilter: Hotel[] = [];
  @Input() productId = 1;
  @Input() currentDates: any;
  @Input() currentFilterDate: any;
  @Input() surveysReady = false;
  @Input() chain: Hotel[] = [];
  @Input() isSuperAdmin = false;

  @Output() loadingChange = new EventEmitter<boolean>();

  loading = false;
  answersRate = 0;
  qualificationAnswersRate: Qualification = 'excellent';
  itemsAnswersRate: Array<{
    id: string;
    label: string;
    percentage: number;
    showIndicator: boolean;
    indicatorColor: string;
    labelTooltip?: string;
  }> = [];
  answersRateData: any = {};
  reportAnswersRate = null;
  reportAnswersRateSlug = 'interchain_followup_summary_answers_rate';
  tooltipGraph: string | null = null;
  leftTextAnswerRate = '';
  leftTextAnswerRateTooltip = '';
  secondButtonText = '';
  secondButtonTooltip = '';
  heigth = '100%';

  constructor(
    private utilService: UtilService,
    private followupPropertiesService: FollowupPropertiesService,
    private translateService: TranslateService,
    private modalService: ModalService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.chainFilter || changes.currentDates || changes.surveysReady) &&
      canLoadData(this.utilService, this.chainFilter, this.surveysReady)
    ) {
      this.loadData();
    }
  }

  loadData(): void {
    this.loading = true;
    this.loadingChange.emit(true);
    const { dates, surveyIds, customerIds, version } = getRequestParams(
      this.utilService,
      this.chainFilter,
      this.currentDates,
    );

    if (surveyIds?.length > 0) {
      this.followupPropertiesService
        .getRate(dates, customerIds, this.productId, surveyIds, false, version)
        .pipe(
          finalize(() => {
            this.loading = false;
            this.loadingChange.emit(false);
          }),
        )
        .subscribe({
          next: (response: any) => this.handleSuccess(response),
          error: () => this.handleError(),
        });
    } else {
      this.handleError();
      this.loading = false;
      this.loadingChange.emit(false);
    }
  }

  private handleSuccess(response: any): void {
    const data = response.average;
    this.answersRate = data?.rate?.actual || 0;
    this.answersRateData = data || {};
    this.qualificationAnswersRate = getQualification(this.answersRate);

    this.itemsAnswersRate = this.buildItems(data);
    this.tooltipGraph = this.buildTooltip(data);
    this.leftTextAnswerRate = '';
    this.leftTextAnswerRateTooltip = '';
  }

  private handleError(): void {
    this.answersRate = 0;
    this.itemsAnswersRate = [];
    this.qualificationAnswersRate = 'bad';
  }

  private buildItems(data: any): any[] {
    return [
      {
        id: 'email',
        label: 'commons.email_short',
        percentage: data?.email_surveys?.rate?.actual || 0,
        showIndicator: true,
        indicatorColor: data?.email_surveys?.status?.toLowerCase() || 'gray',
        labelTooltip: 'followup.summary.data-rate.tooltips.email_answer_rate',
      },
      {
        id: 'whatsapp',
        label: 'commons.whatsapp',
        percentage: data?.whatsapp_surveys?.rate?.actual || 0,
        showIndicator: true,
        indicatorColor: data?.whatsapp_surveys?.status?.toLowerCase() || 'gray',
        labelTooltip: 'followup.summary.data-rate.tooltips.whatsapp_answer_rate',
      },
      {
        id: 'public',
        label: 'followup.summary.summary-survey.title_box.open_surveys',
        percentage: data?.open_surveys?.rate?.actual || 0,
        showIndicator: true,
        indicatorColor: data?.open_surveys?.status?.toLowerCase() || 'gray',
        labelTooltip: 'followup.summary.data-rate.tooltips.interchain_public_answer_rate',
      },
    ];
  }

  private buildTooltip(data: any): string {
    return this.translateService.instant('followup.summary.data-rate.tooltips.answers_rate_graph', {
      answersMail: data?.email_surveys?.answered?.actual || 0,
      answersWsp: data?.whatsapp_surveys?.answered?.actual || 0,
      answersPublic: data?.open_surveys?.answered?.actual || 0,
      sentMail: data?.email_surveys?.sent?.actual || 0,
      sentWsp: (data?.whatsapp_surveys?.sent?.actual || 0) - (data?.whatsapp_surveys?.reminder_sent?.actual || 0),
      openedPublic: data?.open_surveys?.opened?.actual || 0,
    });
  }

  onViewMore(): void {
    if (!this.answersRateData) {
      return;
    }
    this.openModal();
  }

  private openModal(): void {
    const config = {
      data: {
        classes: {
          state: this.qualificationAnswersRate,
        },
        tooltipTranslate: { guests: 'commons.guests', pms: 'PMS', checkout: 'commons.checkout' },
        currentFilter: this.currentFilterDate,
        pms: this.answersRateData,
        hotelName: this.chain[0]?.chain_brand || '',
        productId: this.productId,
        tableRows: this.buildTableRows(),
        tableHeaders: this.buildTableHeaders(),
        customImproveText: 'followup.summary.data-rate.modal.text_improve',
        customTitle: 'followup.summary.data-rate.title',
        customHasA: 'followup.summary.data-rate.modal.has_a',
        coverage: this.answersRate,
        customDescription: this.translateService.instant('followup.summary.data-rate.modal.valid_guests', {
          guests: this.translateService.instant('commons.guests'),
        }),
        showWarnMessage: this.itemsAnswersRate.some((item: any) => item.indicatorColor === 'red'),
      },
    };

    this.utilService.ga('', 'interchain-followup-answersratewidget-detail');
    const classes = ['no-padding', 'overlay-panel', 'lg', 'fit-height-content'];
    this.modalService.open(CoreLibEmailRateModalComponent, config, classes);
  }

  private buildTableHeaders(): any[] {
    return [
      {
        translateKey: 'followup.summary.data-rate.table.channel',
        cssClass: 'tw-py-2 tw-border-b tw-font-medium tw-text-gray-700',
        key: 'col1',
      },
      {
        translateKey: 'followup.summary.data-rate.table.total',
        cssClass: 'tw-py-2 tw-border-b tw-font-medium tw-text-gray-700 tw-text-center',
        key: 'col2',
      },
      {
        translateKey: 'followup.summary.data-rate.table.actual',
        cssClass: 'tw-py-2 tw-border-b tw-font-medium tw-text-gray-700 tw-text-center',
        key: 'col3',
      },
      {
        translateKey: 'followup.summary.data-rate.table.valid',
        cssClass: 'tw-py-2 tw-border-b tw-font-medium tw-text-gray-700 tw-text-center',
        key: 'col4',
      },
      {
        translateKey: 'followup.summary.data-rate.table.rate',
        cssClass: 'tw-py-2 tw-border-b tw-font-medium tw-text-gray-700 tw-text-center',
        key: 'col5',
      },
    ];
  }

  private buildTableRows(): any[] {
    const emailSurvey = this.answersRateData.email_surveys || {
      sent: { actual: 0 },
      opened: { actual: 0 },
      answered: { actual: 0 },
      rate: { actual: 0 },
    };
    const openSurvey = this.answersRateData.open_surveys || {
      sent: { actual: 0 },
      opened: { actual: 0 },
      answered: { actual: 0 },
      rate: { actual: 0 },
    };
    const whatsappSurvey = this.answersRateData.whatsapp_surveys || {
      sent: { actual: 0 },
      opened: { actual: 0 },
      answered: { actual: 0 },
      rate: { actual: 0 },
    };

    return [
      {
        id: 'emails',
        translateKey: 'followup.summary.data-rate.table.email',
        cssClass: '',
        cells: {
          col1: getSimpleCell('followup.summary.data-rate.table.email'),
          col2: getSimpleCell(emailSurvey.sent?.actual || 0, 'tw-text-center'),
          col3: getSimpleCell(emailSurvey.opened?.actual || 0, 'tw-text-center'),
          col4: getSimpleCell(emailSurvey.answered?.actual || 0, 'tw-text-center'),
          col5: getSimpleCell(emailSurvey.rate?.actual || 0, 'tw-text-center', true),
        },
      },
      {
        id: 'phones',
        translateKey: 'followup.summary.data-rate.table.phone',
        cssClass: '',
        cells: {
          col1: getSimpleCell('followup.summary.data-rate.table.phone'),
          col2: getSimpleCell(whatsappSurvey.sent?.actual || 0, 'tw-text-center'),
          col3: getSimpleCell(whatsappSurvey.opened?.actual || 0, 'tw-text-center'),
          col4: getSimpleCell(whatsappSurvey.answered?.actual || 0, 'tw-text-center'),
          col5: getSimpleCell(whatsappSurvey.rate?.actual || 0, 'tw-text-center', true),
        },
      },
      {
        id: 'open',
        translateKey: 'followup.summary.data-rate.table.public',
        cssClass: '',
        cells: {
          col1: getSimpleCell('followup.summary.data-rate.table.public'),
          col2: getSimpleCell('N/A', 'tw-text-center'),
          col3: getSimpleCell(openSurvey.opened?.actual || 0, 'tw-text-center'),
          col4: getSimpleCell(openSurvey.answered?.actual || 0, 'tw-text-center'),
          col5: getSimpleCell(openSurvey.rate?.actual || 0, 'tw-text-center', true),
        },
      },
    ];
  }
}
